import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Lock, Package, Calendar, TrendingUp, Clock, CheckCircle2, XCircle, ChevronDown, LogOut, Eye } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const [authenticated, setAuthenticated] = useState(false);
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [rentRequests, setRentRequests] = useState([]);
  const navigate = useNavigate();

  const loadData = useCallback(async () => {
    try {
      const [statsRes, ordersRes, bookingsRes, rentRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/orders`),
        axios.get(`${API}/admin/bookings`),
        axios.get(`${API}/admin/rent-requests`)
      ]);
      setStats(statsRes.data);
      setOrders(ordersRes.data);
      setBookings(bookingsRes.data);
      setRentRequests(rentRes.data);
    } catch (e) {
      console.error('Failed to load admin data', e);
    }
  }, []);

  useEffect(() => {
    if (authenticated) loadData();
  }, [authenticated, loadData]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/admin/login`, { pin });
      setAuthenticated(true);
      toast.success('Accesso effettuato');
    } catch {
      toast.error('PIN non valido');
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, field, value) => {
    try {
      const params = new URLSearchParams();
      params.append(field, value);
      await axios.patch(`${API}/admin/orders/${orderId}?${params.toString()}`);
      toast.success('Ordine aggiornato');
      loadData();
    } catch {
      toast.error('Errore aggiornamento');
    }
  };

  const updateBookingStatus = async (bookingId, status) => {
    try {
      await axios.patch(`${API}/admin/bookings/${bookingId}?status=${status}`);
      toast.success('Prenotazione aggiornata');
      loadData();
    } catch {
      toast.error('Errore aggiornamento');
    }
  };

  const updateRentStatus = async (requestId, status) => {
    try {
      await axios.patch(`${API}/admin/rent-requests/${requestId}?status=${status}`);
      toast.success('Richiesta sala aggiornata');
      loadData();
    } catch {
      toast.error('Errore aggiornamento');
    }
  };

  if (!authenticated) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-6">
        <div className="w-full max-w-sm" data-testid="admin-login-form">
          <div className="bg-white rounded-3xl p-8 border border-slate-100 shadow-xl shadow-slate-100/50 text-center">
            <div className="w-16 h-16 rounded-full bg-[#0288D1]/10 flex items-center justify-center mx-auto mb-4">
              <Lock size={28} className="text-[#0288D1]" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-slate-900 mb-1">Pannello Admin</h1>
            <p className="text-sm text-slate-500 mb-6">Inserisci il PIN per accedere</p>
            <form onSubmit={handleLogin} className="space-y-4">
              <Input
                type="password"
                data-testid="admin-pin-input"
                value={pin}
                onChange={e => setPin(e.target.value)}
                placeholder="PIN di accesso"
                className="text-center text-lg tracking-widest"
                required
              />
              <button
                type="submit"
                data-testid="admin-login-btn"
                disabled={loading}
                className="w-full py-3 bg-[#0288D1] text-white font-heading font-bold rounded-full btn-caribbean btn-blue disabled:opacity-50"
              >
                {loading ? 'Accesso...' : 'Accedi'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Admin Header */}
      <div className="bg-slate-900 text-white py-4 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="font-heading text-xl font-bold">Pannello Admin</h1>
            <p className="text-slate-400 text-sm">Caribbean Blue Moon Dance School</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/')}
              data-testid="admin-go-site"
              className="px-4 py-2 bg-slate-800 text-slate-300 text-sm rounded-full hover:bg-slate-700 transition-colors"
            >
              Vai al sito
            </button>
            <button
              onClick={() => setAuthenticated(false)}
              data-testid="admin-logout"
              className="p-2 hover:bg-slate-800 rounded-full transition-colors"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
            <StatCard icon={<Package size={20} />} label="Ordini Totali" value={stats.total_orders} color="blue" />
            <StatCard icon={<Clock size={20} />} label="Ordini in Attesa" value={stats.pending_orders} color="orange" />
            <StatCard icon={<Calendar size={20} />} label="Prenotazioni" value={stats.total_bookings} color="green" />
            <StatCard icon={<TrendingUp size={20} />} label="Fatturato" value={`€${stats.total_revenue.toFixed(2)}`} color="purple" />
            <StatCard icon={<Eye size={20} />} label="Richieste Sala" value={stats.total_rent_requests || 0} color="blue" />
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="orders" data-testid="admin-tabs">
          <TabsList className="mb-6">
            <TabsTrigger value="orders" className="font-heading font-semibold">
              Ordini ({orders.length})
            </TabsTrigger>
            <TabsTrigger value="bookings" className="font-heading font-semibold">
              Prenotazioni ({bookings.length})
            </TabsTrigger>
            <TabsTrigger value="rent" className="font-heading font-semibold">
              Richieste Sala ({rentRequests.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="orders">
            {orders.length === 0 ? (
              <EmptyState text="Nessun ordine ancora" />
            ) : (
              <div className="space-y-3">
                {orders.map(order => (
                  <div
                    key={order.id}
                    data-testid={`admin-order-${order.id}`}
                    className="bg-white rounded-2xl p-4 md:p-6 border border-slate-100 shadow-md shadow-slate-100/30"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                      <div>
                        <p className="font-heading font-bold text-slate-900">{order.customer_name}</p>
                        <p className="text-sm text-slate-500">{order.customer_email} | {order.customer_phone}</p>
                        <p className="text-xs text-slate-400 mt-1">
                          {new Date(order.created_at).toLocaleString('it-IT')} | {order.payment_method === 'stripe' ? 'Carta' : 'Bonifico'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <StatusBadge status={order.status} />
                        <PaymentBadge status={order.payment_status} />
                        <span className="font-heading font-bold text-lg text-[#FF7043]">€{order.total?.toFixed(2)}</span>
                      </div>
                    </div>

                    {/* Items preview */}
                    <div className="flex flex-wrap gap-2 mb-3">
                      {order.items?.map((item, i) => (
                        <span key={i} className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full">
                          {item.product_name?.replace(' Caribbean Blue Moon', '')} x{item.quantity}
                          {item.size && ` (${item.size})`}
                        </span>
                      ))}
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <Select onValueChange={(v) => updateOrderStatus(order.id, 'status', v)}>
                        <SelectTrigger className="w-40 h-8 text-xs" data-testid={`order-status-${order.id}`}>
                          <SelectValue placeholder="Stato ordine" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">In Attesa</SelectItem>
                          <SelectItem value="confirmed">Confermato</SelectItem>
                          <SelectItem value="shipped">Spedito</SelectItem>
                          <SelectItem value="delivered">Consegnato</SelectItem>
                          <SelectItem value="cancelled">Annullato</SelectItem>
                        </SelectContent>
                      </Select>
                      <Select onValueChange={(v) => updateOrderStatus(order.id, 'payment_status', v)}>
                        <SelectTrigger className="w-40 h-8 text-xs" data-testid={`order-payment-${order.id}`}>
                          <SelectValue placeholder="Stato pagamento" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">In Attesa</SelectItem>
                          <SelectItem value="paid">Pagato</SelectItem>
                          <SelectItem value="refunded">Rimborsato</SelectItem>
                        </SelectContent>
                      </Select>
                      <button
                        onClick={() => setSelectedOrder(order)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-[#0288D1] bg-blue-50 rounded-full hover:bg-blue-100 transition-colors"
                      >
                        <Eye size={12} /> Dettagli
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="bookings">
            {bookings.length === 0 ? (
              <EmptyState text="Nessuna prenotazione ancora" />
            ) : (
              <div className="space-y-3">
                {bookings.map(booking => (
                  <div
                    key={booking.id}
                    data-testid={`admin-booking-${booking.id}`}
                    className="bg-white rounded-2xl p-4 md:p-6 border border-slate-100 shadow-md shadow-slate-100/30"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                      <div>
                        <p className="font-heading font-bold text-slate-900">{booking.student_name}</p>
                        <p className="text-sm text-slate-500">{booking.email} | {booking.phone}</p>
                        {booking.parent_name && (
                          <p className="text-xs text-slate-400">Genitore: {booking.parent_name}</p>
                        )}
                        {booking.student_age && (
                          <p className="text-xs text-slate-400">Età: {booking.student_age}</p>
                        )}
                        <p className="text-xs text-slate-400 mt-1">
                          {new Date(booking.created_at).toLocaleString('it-IT')}
                        </p>
                      </div>
                      <div className="text-right">
                        <span className="inline-block px-3 py-1 bg-[#0288D1]/10 text-[#0288D1] text-xs font-bold rounded-full mb-1">
                          {booking.course_name}
                        </span>
                        <div className="mt-1">
                          <StatusBadge status={booking.status} />
                        </div>
                      </div>
                    </div>

                    {booking.notes && (
                      <p className="text-xs text-slate-500 bg-slate-50 rounded-xl p-3 mb-3">
                        Note: {booking.notes}
                      </p>
                    )}

                    <div className="flex items-center gap-2">
                      <Select onValueChange={(v) => updateBookingStatus(booking.id, v)}>
                        <SelectTrigger className="w-44 h-8 text-xs" data-testid={`booking-status-${booking.id}`}>
                          <SelectValue placeholder="Aggiorna stato" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">In Attesa</SelectItem>
                          <SelectItem value="contacted">Contattato</SelectItem>
                          <SelectItem value="confirmed">Confermato</SelectItem>
                          <SelectItem value="completed">Completato</SelectItem>
                          <SelectItem value="cancelled">Annullato</SelectItem>
                        </SelectContent>
                      </Select>
                      <a
                        href={`tel:${booking.phone}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-green-700 bg-green-50 rounded-full hover:bg-green-100 transition-colors"
                      >
                        Chiama
                      </a>
                      <a
                        href={`mailto:${booking.email}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-[#0288D1] bg-blue-50 rounded-full hover:bg-blue-100 transition-colors"
                      >
                        Email
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="rent">
            {rentRequests.length === 0 ? (
              <EmptyState text="Nessuna richiesta sala ancora" />
            ) : (
              <div className="space-y-3">
                {rentRequests.map(req => (
                  <div
                    key={req.id}
                    data-testid={`admin-rent-${req.id}`}
                    className="bg-white rounded-2xl p-4 md:p-6 border border-slate-100 shadow-md shadow-slate-100/30"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                      <div>
                        <p className="font-heading font-bold text-slate-900">{req.name}</p>
                        <p className="text-sm text-slate-500">{req.email} | {req.phone}</p>
                        <p className="text-xs text-slate-400 mt-1">
                          {new Date(req.created_at).toLocaleString('it-IT')}
                        </p>
                      </div>
                      <div className="text-right">
                        <span className="inline-block px-3 py-1 bg-[#FF7043]/10 text-[#FF7043] text-xs font-bold rounded-full mb-1">
                          {req.event_type}
                        </span>
                        <div className="mt-1">
                          <StatusBadge status={req.status} />
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 mb-3 text-xs text-slate-500">
                      {req.date && <span className="bg-slate-50 px-2 py-1 rounded-full">Data: {req.date}</span>}
                      {req.guests && <span className="bg-slate-50 px-2 py-1 rounded-full">Invitati: ~{req.guests}</span>}
                    </div>
                    {req.message && (
                      <p className="text-xs text-slate-500 bg-slate-50 rounded-xl p-3 mb-3">{req.message}</p>
                    )}
                    <div className="flex items-center gap-2">
                      <Select onValueChange={(v) => updateRentStatus(req.id, v)}>
                        <SelectTrigger className="w-44 h-8 text-xs">
                          <SelectValue placeholder="Aggiorna stato" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">In Attesa</SelectItem>
                          <SelectItem value="contacted">Contattato</SelectItem>
                          <SelectItem value="confirmed">Confermato</SelectItem>
                          <SelectItem value="completed">Completato</SelectItem>
                          <SelectItem value="cancelled">Annullato</SelectItem>
                        </SelectContent>
                      </Select>
                      <a
                        href={`tel:${req.phone}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-green-700 bg-green-50 rounded-full hover:bg-green-100 transition-colors"
                      >
                        Chiama
                      </a>
                      <a
                        href={`mailto:${req.email}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-[#0288D1] bg-blue-50 rounded-full hover:bg-blue-100 transition-colors"
                      >
                        Email
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Order Detail Dialog */}
      <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Dettagli Ordine</DialogTitle>
            <DialogDescription>
              ID: {selectedOrder?.id?.slice(0, 8)}...
            </DialogDescription>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-4 mt-2">
              <div className="bg-slate-50 rounded-xl p-4 space-y-2 text-sm">
                <p><span className="text-slate-500">Cliente:</span> <strong>{selectedOrder.customer_name}</strong></p>
                <p><span className="text-slate-500">Email:</span> {selectedOrder.customer_email}</p>
                <p><span className="text-slate-500">Telefono:</span> {selectedOrder.customer_phone}</p>
                <p><span className="text-slate-500">Indirizzo:</span> {selectedOrder.shipping_address}</p>
                {selectedOrder.notes && <p><span className="text-slate-500">Note:</span> {selectedOrder.notes}</p>}
                <p><span className="text-slate-500">Metodo:</span> {selectedOrder.payment_method === 'stripe' ? 'Carta di Credito' : 'Bonifico'}</p>
              </div>
              <div className="space-y-2">
                <p className="font-heading font-semibold">Prodotti:</p>
                {selectedOrder.items?.map((item, i) => (
                  <div key={i} className="flex justify-between text-sm bg-white rounded-lg p-2 border">
                    <span>{item.product_name?.replace(' Caribbean Blue Moon', '')} {item.size && `(${item.size})`} x{item.quantity}</span>
                    <span className="font-semibold">€{item.subtotal?.toFixed(2)}</span>
                  </div>
                ))}
                <div className="flex justify-between font-heading font-bold text-lg pt-2 border-t">
                  <span>Totale</span>
                  <span className="text-[#FF7043]">€{selectedOrder.total?.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon, label, value, color }) {
  const colors = {
    blue: 'bg-[#0288D1]/10 text-[#0288D1]',
    orange: 'bg-[#FF7043]/10 text-[#FF7043]',
    green: 'bg-green-100 text-green-700',
    purple: 'bg-purple-100 text-purple-700'
  };
  return (
    <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-md shadow-slate-100/30">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${colors[color]}`}>{icon}</div>
      <p className="text-2xl font-heading font-bold text-slate-900">{value}</p>
      <p className="text-xs text-slate-500 mt-1">{label}</p>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: { label: 'In Attesa', cls: 'bg-yellow-100 text-yellow-800' },
    confirmed: { label: 'Confermato', cls: 'bg-green-100 text-green-800' },
    contacted: { label: 'Contattato', cls: 'bg-blue-100 text-blue-800' },
    shipped: { label: 'Spedito', cls: 'bg-indigo-100 text-indigo-800' },
    delivered: { label: 'Consegnato', cls: 'bg-green-200 text-green-900' },
    completed: { label: 'Completato', cls: 'bg-green-200 text-green-900' },
    cancelled: { label: 'Annullato', cls: 'bg-red-100 text-red-800' },
  };
  const s = map[status] || { label: status, cls: 'bg-slate-100 text-slate-800' };
  return <span className={`inline-block px-2.5 py-1 text-xs font-bold rounded-full ${s.cls}`}>{s.label}</span>;
}

function PaymentBadge({ status }) {
  const map = {
    pending: { label: 'Non Pagato', cls: 'bg-yellow-100 text-yellow-800' },
    paid: { label: 'Pagato', cls: 'bg-green-100 text-green-800' },
    refunded: { label: 'Rimborsato', cls: 'bg-red-100 text-red-800' },
  };
  const s = map[status] || { label: status, cls: 'bg-slate-100 text-slate-800' };
  return <span className={`inline-block px-2.5 py-1 text-xs font-bold rounded-full ${s.cls}`}>{s.label}</span>;
}

function EmptyState({ text }) {
  return (
    <div className="text-center py-16">
      <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
        <Package size={24} className="text-slate-400" />
      </div>
      <p className="text-slate-500 font-heading">{text}</p>
    </div>
  );
}
